/**
 * G02. Boss FSM — Node-State refactor of boss AI.
 *
 * Wraps a BossDef with a FSM-driven `tickAttacks` that produces BYTE-EQUIVALENT
 * output to the legacy cooldown-driven `tickAttacks` closures in boss-pool.ts.
 *
 * Architecture
 * ------------
 * The legacy boss-pool files (boss-pool.ts, boss-tier2-pool.ts, boss-tier3-pool.ts)
 * inline a `tickAttacks(self, nowMs, playerX)` closure on each BossDef. Each
 * closure has the same canonical shape:
 *
 *   1. Spawning gate:  `if (self.y < self.def.size / 2) return []`
 *   2. Attack-A:       cooldown-driven, phase-dependent payload
 *   3. Attack-B:       cooldown-driven, phase-gated, phase-dependent payload
 *
 * This module formalises that shape as a FSM with four canonical states:
 *
 *   - 'spawning'   — waiting for the boss to descend past its sprite radius
 *   - 'attacking'  — main combat loop, runs attack-A / attack-B with cooldowns
 *   - 'berserk'    — same logic as 'attacking' but berserk-tinted (visual hook)
 *   - 'dead'       — terminal, produces no attacks
 *
 * The state-machine itself only decides *which state we are in*; the per-phase
 * attack payloads come from a declarative `BossAttackPattern` plugged into
 * `wrapBossDefWithFSM(def, patterns)`.
 *
 * Parity guarantee
 * ----------------
 * Given identical inputs (nowMs sequence, playerX, takeDamage timing), the
 * wrapped def's tickAttacks output MUST match the legacy closure's output
 * exactly (same kinds, same payload values, same order). The parity test
 * (tests/game/ai/boss-fsm-parity.test.ts) enforces this for all 9 bosses.
 *
 * Why this design (not "1 state per attack")
 * ------------------------------------------
 * The Wiki spec proposes Idle/Telegraph/Burst/Stunned as the canonical 4 states.
 * Our existing combat is *cooldown-driven*, not *animation-driven* — there is no
 * dedicated telegraph phase between cooldown-expiry and bullet-spawn (telegraph
 * happens via `attackBWarningMs` on a separate channel). Modelling each attack
 * as its own state would require restructuring scene-side warning-ring code,
 * which the boss-fsm task explicitly forbids. Keeping the 4 canonical lifecycle
 * states (spawning / attacking / berserk / dead) preserves parity while exposing
 * a state-graph that future bosses (e.g. demo boss with stun-recoil) can extend.
 */

import type { Boss, BossDef, BossAttack } from '../entities/boss';
import { IState, StateMachine } from './StateMachine';

// ── Declarative attack patterns ──────────────────────────────────────────────

/**
 * Per-phase attack payload producer. Returns the attacks emitted on a single
 * cooldown-expiry tick. Returning [] means "skip this attack channel this tick".
 *
 * `nowMs` and `playerX` mirror the legacy `tickAttacks` parameters. The pattern
 * is responsible for using `playerX` to compute aim angles where applicable.
 */
export type BossAttackPattern = (self: Boss, nowMs: number, playerX: number) => BossAttack[];

/**
 * Per-boss FSM configuration.
 *
 * `attackA[phase]` and `attackB[phase]` map 1-indexed phase numbers to payload
 * producers. Phases without an entry produce no attack on that channel.
 *
 * `attackBPhaseGate`: if set, attack-B fires only when `self.phase >= gate`.
 * This preserves the legacy "if (self.phase === 2 && ...)" pattern. Defaults
 * to 1 (no gate) — multi-phase bosses (tier-3) typically use 1 since their
 * patterns vary per-phase rather than being phase-gated.
 */
export interface BossFSMConfig {
  attackA: Record<number, BossAttackPattern>;
  attackB?: Record<number, BossAttackPattern>;
  /** If set, attack-B fires only when self.phase >= this value. Default 1. */
  attackBPhaseGate?: number;
  /**
   * Escape hatch for bosses whose legacy tickAttacks is no longer the canonical
   * cooldown-channel shape. Since 138e54b0 (learnable-boss-proto), SHU_MO's
   * legacy closure is itself a pattern FSM (boss-pattern.ts: IDLE→TELEGRAPH→
   * FIRE→RECOVER) and cannot be expressed as attackA/attackB channels.
   * When set, the attacking/berserk states call `customTick` once per tick and
   * SKIP the cooldown channels entirely (spawning/dead gating still applies).
   */
  customTick?: BossAttackPattern;
}

// ── FSM context ──────────────────────────────────────────────────────────────

/**
 * Mutable context passed to each state's enter/update/exit hooks.
 *
 * The state machine is driven from `tickAttacks`, not from a per-frame `update`
 * loop, because boss attacks are scene-driven (scene calls `def.tickAttacks`
 * once per frame). We piggy-back state transitions onto those calls so the
 * legacy integration point stays untouched.
 */
interface BossFSMContext {
  boss: Boss;
  config: BossFSMConfig;
  /** Output buffer — states push attacks here during update. */
  out: BossAttack[];
  /** Current tick params (set by tickAttacks before sm.update). */
  nowMs: number;
  playerX: number;
  /** State machine reference for self-transition. */
  sm: StateMachine<BossFSMContext>;
}

// ── States ──────────────────────────────────────────────────────────────────

class SpawningState implements IState<BossFSMContext> {
  readonly name = 'spawning';
  enter(_ctx: BossFSMContext): void {
    // No-op: spawn animation owned by scene
  }
  update(ctx: BossFSMContext, dt: number): void {
    const { boss } = ctx;
    if (boss.y >= boss.def.size / 2) {
      ctx.sm.transitionTo('attacking');
      // Re-dispatch this tick so we don't drop a frame relative to legacy
      // (legacy: `if (y < size/2) return [] else { ...attack logic }`).
      ctx.sm.update(dt);
    }
  }
  exit(_ctx: BossFSMContext): void {
    // No-op
  }
}

class AttackingState implements IState<BossFSMContext> {
  readonly name = 'attacking';
  enter(_ctx: BossFSMContext): void {
    // No-op: cooldowns persist on Boss instance across state transitions
  }
  update(ctx: BossFSMContext, _dt: number): void {
    const { boss, config, out, nowMs, playerX } = ctx;
    // Death check — transition to terminal
    if (!boss.alive) {
      ctx.sm.transitionTo('dead');
      return;
    }
    // Berserk auto-transition (visual hook; logic is identical)
    if (boss.berserkMode) {
      ctx.sm.transitionTo('berserk');
      // Don't return — re-dispatch via new state's update so this tick still produces attacks
      const newCurrent = ctx.sm.currentName;
      if (newCurrent === 'berserk') {
        // berserk state will be entered next frame; for THIS tick continue with attack logic
        // to preserve byte-equivalence with legacy (which never had this gating).
      }
    }
    runAttackChannels(ctx, boss, config, out, nowMs, playerX);
  }
  exit(_ctx: BossFSMContext): void {
    // No-op
  }
}

class BerserkState implements IState<BossFSMContext> {
  readonly name = 'berserk';
  enter(_ctx: BossFSMContext): void {
    // Visual berserk overlay is driven by Boss.berserkMode, not by FSM state.
    // This state exists so the FSM graph reflects the gameplay phase cleanly,
    // making it easy for future bosses to extend (e.g. demo boss can attach
    // extra payloads on berserk enter).
  }
  update(ctx: BossFSMContext, _dt: number): void {
    const { boss, config, out, nowMs, playerX } = ctx;
    if (!boss.alive) {
      ctx.sm.transitionTo('dead');
      return;
    }
    runAttackChannels(ctx, boss, config, out, nowMs, playerX);
  }
  exit(_ctx: BossFSMContext): void {
    // No-op
  }
}

class DeadState implements IState<BossFSMContext> {
  readonly name = 'dead';
  enter(_ctx: BossFSMContext): void {
    // No-op: scene handles death cinematic
  }
  update(_ctx: BossFSMContext, _dt: number): void {
    // No attacks while dead
  }
  exit(_ctx: BossFSMContext): void {
    // No-op
  }
}

// ── Attack channel runner ────────────────────────────────────────────────────

/**
 * Run attack-A and attack-B channels with cooldown gating. Mirrors the legacy
 * boss-pool closure shape EXACTLY so wrapped output matches byte-for-byte:
 *
 *   1. attack-A: if nowMs - lastA >= effectiveA, push pattern[phase] result
 *   2. attack-B: if phase >= gate AND nowMs - lastB >= effectiveB, push pattern[phase] result
 *
 * Order of pushes matches legacy (attack-A first, attack-B second).
 */
function runAttackChannels(
  _ctx: BossFSMContext,
  boss: Boss,
  config: BossFSMConfig,
  out: BossAttack[],
  nowMs: number,
  playerX: number,
): void {
  // Pattern-driven boss (e.g. SHU_MO since 138e54b0) — delegate wholesale.
  if (config.customTick) {
    for (const atk of config.customTick(boss, nowMs, playerX)) out.push(atk);
    return;
  }
  // Attack-A
  const patternA = config.attackA[boss.phase];
  if (patternA && nowMs - boss.lastAttackAMs >= boss.effectiveAttackACooldownMs) {
    boss.lastAttackAMs = nowMs;
    for (const atk of patternA(boss, nowMs, playerX)) out.push(atk);
  }
  // Attack-B
  if (config.attackB) {
    const gate = config.attackBPhaseGate ?? 1;
    const patternB = config.attackB[boss.phase];
    if (patternB && boss.phase >= gate && nowMs - boss.lastAttackBMs >= boss.effectiveAttackBCooldownMs) {
      boss.lastAttackBMs = nowMs;
      for (const atk of patternB(boss, nowMs, playerX)) out.push(atk);
    }
  }
}

// ── FSM factory ──────────────────────────────────────────────────────────────

/**
 * Build a per-boss state-machine pre-loaded with the canonical 4 states.
 *
 * Each boss instance gets its own SM (states are stateless singletons but the
 * machine holds the current pointer + context). The SM is lazily created on
 * first tickAttacks call inside the wrapper.
 */
function buildBossSM(boss: Boss, config: BossFSMConfig): {
  sm: StateMachine<BossFSMContext>;
  ctx: BossFSMContext;
} {
  const ctx: BossFSMContext = {
    boss,
    config,
    out: [],
    nowMs: 0,
    playerX: 0,
    // sm assigned below
    sm: null as unknown as StateMachine<BossFSMContext>,
  };
  const sm = new StateMachine<BossFSMContext>(ctx);
  ctx.sm = sm;
  sm.add(new SpawningState());
  sm.add(new AttackingState());
  sm.add(new BerserkState());
  sm.add(new DeadState());
  sm.transitionTo('spawning');
  return { sm, ctx };
}

// ── Per-boss SM cache (WeakMap keyed by Boss instance) ──────────────────────

const SM_CACHE = new WeakMap<Boss, { sm: StateMachine<BossFSMContext>; ctx: BossFSMContext }>();

/**
 * Wrap a BossDef with a FSM-driven tickAttacks. The returned def is a shallow
 * clone of the input with `tickAttacks` replaced.
 *
 * Usage:
 *   const fsmDef = wrapBossDefWithFSM(SHU_MO_LEGACY, SHU_MO_FSM_CONFIG);
 *   // fsmDef.tickAttacks(boss, nowMs, playerX) produces same output as legacy
 *
 * Parity contract: for any (nowMs sequence, playerX, damage timing), the output
 * of fsmDef.tickAttacks must equal the output of the input def's tickAttacks.
 */
export function wrapBossDefWithFSM(def: BossDef, config: BossFSMConfig): BossDef {
  return {
    ...def,
    tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
      let cache = SM_CACHE.get(self);
      if (!cache) {
        cache = buildBossSM(self, config);
        SM_CACHE.set(self, cache);
      }
      const { sm, ctx } = cache;
      ctx.out = [];
      ctx.nowMs = nowMs;
      ctx.playerX = playerX;
      // The FSM doesn't need a real dt for cooldown-driven combat; we pass 0.
      // Cooldowns live on the Boss instance (lastAttackA/BMs) not on states.
      sm.update(0);
      return ctx.out;
    },
  };
}

/**
 * Inspect the current FSM state of a wrapped boss (test/debug helper).
 * Returns undefined if the boss has not yet had tickAttacks called.
 */
export function getBossFSMState(boss: Boss): string | undefined {
  return SM_CACHE.get(boss)?.sm.currentName;
}
